import { StrainTypeFilterOptions } from 'shared/constants';
import type { ProductsFilterInput as GraphqlProductsFilter } from 'src/gql/graphql';
import type { ProductsFilterInput as ResolvedProductsFilter } from 'src/dispensary/hooks/use-dispensary-products';
import type { ConciergeMessageSeed, ProductSearchContext } from 'src/concierge/contract/actions';
import { MAX_CONCIERGE_SEED_MESSAGE_LENGTH } from 'src/concierge/concierge-message-seed-storage';
import { getEffectsAsOptions } from 'src/utils/helpers/effects-filtering';

/** Maximum items per list field accepted by consumer-server's product_search context. */
export const MAX_PRODUCT_SEARCH_CONTEXT_LIST_ITEMS = 20;

type ContextFilterKeys =
  | 'brandIds'
  | 'CBDContent'
  | 'option'
  | 'strainTypes'
  | 'subcategories'
  | 'terpenes'
  | 'THCContent'
  | 'types';

// The live hook accepts a scalar subcategory as well as a list; GraphQL coerces it to a list.
export type CanonicalProductsFilter =
  | Pick<GraphqlProductsFilter, ContextFilterKeys>
  | Pick<ResolvedProductsFilter, ContextFilterKeys>;

type ContextInput = {
  // The filter actually sent to filteredProducts, not values reconstructed from URL slugs.
  resolvedFilter: CanonicalProductsFilter;
  selectedEffects?: readonly string[];
  menuEffects?: readonly string[];
  menuTerpenes?: ReadonlyArray<{ name: string; compound_id?: string | null }>;
};

type PotencyUnit = NonNullable<ProductSearchContext['thcUnit']>;

type PotencyContent = CanonicalProductsFilter['THCContent'];

type SearchContentInput = { searchTerm: string; hasFilters: boolean; context: ProductSearchContext };

type CategoryContentInput = { categoryName: string; context: ProductSearchContext };

type ProductListConciergeContent = {
  sectionLabel: string;
  headline: string;
  subtext: string;
  button: string;
  seed: ConciergeMessageSeed | null;
};

export type ProductListConciergeEntry = {
  content: ProductListConciergeContent;
  onClick: () => void;
};

type CardCopy = Pick<ProductListConciergeContent, 'headline' | 'subtext'> & { message: string };

function potencyPair(content: PotencyContent): { range: [number, number]; unit: PotencyUnit } | null {
  const range = content?.range;
  const unit = content?.unit;
  if (
    range?.length !== 2 ||
    typeof range[0] !== 'number' ||
    typeof range[1] !== 'number' ||
    !Number.isFinite(range[0]) ||
    !Number.isFinite(range[1]) ||
    range[0] < 0 ||
    range[0] > range[1] ||
    (unit !== 'PERCENTAGE' && unit !== 'MILLIGRAMS')
  ) {
    return null;
  }
  return { range: [range[0], range[1]], unit };
}

function selectedStrings(values?: string | readonly (string | null)[] | null): string[] {
  const selections = typeof values === 'string' ? [values] : values ?? [];
  return selections.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
}

function limitContextList<T>(values: T[]): T[] {
  return [...new Set(values)].slice(0, MAX_PRODUCT_SEARCH_CONTEXT_LIST_ITEMS);
}

function categoryContext(categories: string[], subcategories: string[]): ProductSearchContext {
  // The consumer server requires one category to interpret subcategories.
  if (categories.length !== 1) return {};

  const [category] = categories;
  return { category, ...(subcategories.length ? { subcategories: limitContextList(subcategories) } : {}) };
}

function selectedStrainTypes(values: CanonicalProductsFilter['strainTypes']): string[] {
  const validTypes = new Set(StrainTypeFilterOptions.map(({ value }) => value));
  return selectedStrings(values).filter((value) => validTypes.has(value));
}

function selectedMenuEffects(
  selectedEffects: ContextInput['selectedEffects'],
  menuEffects: ContextInput['menuEffects']
): string[] {
  const selected = new Set(selectedEffects ?? []);
  return (menuEffects ?? []).flatMap((effect) => {
    // Match the selected UI value to the effect string returned by menuFiltersV2.
    try {
      const [option] = getEffectsAsOptions([effect]);
      return selected.has(option.value) ? [effect] : [];
    } catch {
      return [];
    }
  });
}

function selectedTerpeneNames(
  ids: CanonicalProductsFilter['terpenes'],
  menuTerpenes: ContextInput['menuTerpenes']
): string[] {
  const selectedIds = new Set(selectedStrings(ids));
  return (menuTerpenes ?? [])
    .filter(({ compound_id: compoundId }) => !!compoundId && selectedIds.has(compoundId))
    .map(({ name }) => name);
}

function selectedFilterContext(input: ContextInput): ProductSearchContext {
  const { resolvedFilter } = input;
  const strainTypes = limitContextList(selectedStrainTypes(resolvedFilter.strainTypes));
  const brandIds = limitContextList(selectedStrings(resolvedFilter.brandIds));
  const effects = limitContextList(selectedMenuEffects(input.selectedEffects, input.menuEffects));
  const terpenes = limitContextList(selectedTerpeneNames(resolvedFilter.terpenes, input.menuTerpenes));
  const weight = resolvedFilter.option;

  return {
    ...(strainTypes.length ? { strainTypes } : {}),
    ...(brandIds.length ? { brandIds } : {}),
    ...(effects.length ? { effects } : {}),
    ...(terpenes.length ? { terpenes } : {}),
    ...(typeof weight === 'string' && weight.trim() ? { weight } : {}),
  };
}

function potencyContext(productsFilter: CanonicalProductsFilter): ProductSearchContext {
  const thc = potencyPair(productsFilter.THCContent);
  const cbd = potencyPair(productsFilter.CBDContent);
  return {
    ...(thc ? { thcRange: thc.range, thcUnit: thc.unit } : {}),
    ...(cbd ? { cbdRange: cbd.range, cbdUnit: cbd.unit } : {}),
  };
}

/** Only fields understood by consumer-server's product_search context; no sort, special or query text. */
export function buildProductSearchContext(input: ContextInput): ProductSearchContext {
  const { resolvedFilter } = input;
  const categories = selectedStrings(resolvedFilter.types);
  const subcategories = selectedStrings(resolvedFilter.subcategories);
  return {
    ...categoryContext(categories, subcategories),
    ...selectedFilterContext(input),
    ...potencyContext(resolvedFilter),
  };
}

function categoryCopy(categoryName: string): CardCopy {
  return {
    headline: `Ask the AI Budtender about ${categoryName}`,
    subtext: `Get personalized ${categoryName} picks in seconds.`,
    message: `Help me find the best ${categoryName} products for me.`,
  };
}

function searchCopy(searchTerm: string, hasFilters: boolean): CardCopy {
  const subtext = 'Chat with our AI Budtender for personalized picks.';
  if (searchTerm.trim()) {
    return { headline: `Go deeper on "${searchTerm}"`, subtext, message: `Find products for "${searchTerm}".` };
  }
  if (hasFilters) {
    return {
      headline: 'Narrow down your options',
      subtext,
      message: 'Find me products that match the filters I had applied.',
    };
  }
  return { headline: 'Discover picks for you', subtext, message: 'Show me products personalized for me.' };
}

function createSeed(message: string, context: ProductSearchContext): ConciergeMessageSeed | null {
  if (message.length > MAX_CONCIERGE_SEED_MESSAGE_LENGTH) return null;
  return { kind: 'product_search', message, ...(Object.keys(context).length ? { context } : {}) };
}

function buildContent(
  { headline, subtext, message }: CardCopy,
  context: ProductSearchContext
): ProductListConciergeContent {
  return {
    sectionLabel: 'AI CONCIERGE',
    headline,
    subtext,
    button: 'Ask AI Concierge',
    seed: createSeed(message, context),
  };
}

/** Search-page card text and one-shot seed, without side effects. */
export function buildSearchConciergeContent({
  searchTerm,
  hasFilters,
  context,
}: SearchContentInput): ProductListConciergeContent {
  return buildContent(searchCopy(searchTerm, hasFilters), context);
}

/** Category-page card text and one-shot seed, without side effects. */
export function buildCategoryConciergeContent({
  categoryName,
  context,
}: CategoryContentInput): ProductListConciergeContent {
  return buildContent(categoryCopy(categoryName), context);
}
